import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq, or } from 'ember-truth-helpers';
import dateFormat from 'garaje/helpers/date-format';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';
import type ScreenerReview from 'garaje/models/screener-review';
import { reads } from 'macro-decorators';

interface BlocklistColumnComponentSignature {
  Args: {
    content: Invite | Entry;
  };
}

export default class BlocklistColumnComponent extends Component<BlocklistColumnComponentSignature> {
  @reads('args.content.currentApprovalStatus') status!: this['args']['content']['currentApprovalStatus'];
  @reads('review.reviewerName') reviewerName!: string;
  @reads('review.reviewedAt') reviewedAt!: string | Date;

  get review(): ScreenerReview | undefined {
    return this.args.content.visitApproval?.screenerReviews?.slice().at(-1);
  }

  get isEnabled(): boolean {
    return Boolean(this.args.content.visitApproval?.blocklistResult);
  }

  get isBlocklistEntry(): boolean {
    if (!this.isEnabled) return false;

    const result = this.args.content.visitApproval?.blocklistResult;
    return Boolean(result?.didFail);
  }

  <template>
    <div class="table-cell border-b align-top pt-6 px-2 text-carbon-50 whitespace-nowrap" data-test-blocklist-column>
      <div data-test-blocklist-status data-test-blocklist-status-value={{this.status}}>
        {{#if this.isEnabled}}
          {{#if this.isBlocklistEntry}}
            {{#if (eq this.status "pending")}}
              <img src="/assets/images/gray-dash-small.svg" alt="" role="presentation" />&nbsp;
              <span class="whitespace-nowrap" data-test-status>
                Pending
              </span>
            {{else if (eq this.status "denied")}}
              <EmberTooltip @side="top">
                By
                {{or this.reviewerName "an Administrator"}}
                on
                {{dateFormat this.reviewedAt "PP 'at' p"}}
              </EmberTooltip>
              <img
                src="/assets/images/cross-small-red.svg"
                alt=""
                style="width:10px; height:10px;"
                role="presentation"
              />&nbsp;
              <span class="whitespace-nowrap" data-test-status>
                Access denied
              </span>
            {{else if (eq this.status "approved")}}
              <EmberTooltip @side="top">
                By
                {{or this.reviewerName "system"}}
                {{if this.reviewedAt (concat "on " (dateFormat this.reviewedAt "PP 'at' p"))}}
              </EmberTooltip>
              <img
                src="/assets/images/checkmark-small-gray.svg"
                alt=""
                style="width:11px; height:10px;"
                role="presentation"
              />&nbsp;
              <span class="whitespace-nowrap" data-test-status>
                Access approved
              </span>
            {{/if}}
          {{else}}
            <EmberTooltip @side="top">
              No block list keywords matched
            </EmberTooltip>
            <img src="/assets/images/checkmark-small-gray.svg" alt="" role="presentation" />&nbsp;
            <span class="whitespace-nowrap" data-test-status>
              No match
            </span>
          {{/if}}
        {{else}}
          <EmberTooltip @side="top">
            Block list was not enabled
          </EmberTooltip>
          <img src="/assets/images/gray-dash-small.svg" alt="" role="presentation" />&nbsp;
          <span class="whitespace-nowrap" data-test-status>
            Not enabled
          </span>
        {{/if}}
      </div>
    </div>
  </template>
}
